/**
 * Custom JavaScript for Chakkraa. Loaded (defer) after theme.min.js.
 *
 * Theme hooks available on `document`:
 *   document.addEventListener('variant:changed', function (event) { ... });
 *   document.addEventListener('product:added', function (event) { ... });
 */
(function () {
  'use strict';

  /**
   * Announcement bar — rotate messages.
   * Markup: sections/announcement.liquid. Activates only when the bar has
   * data-announcement-autoplay="true" and 2+ messages. With JS off, the
   * first message (.is-active) stays visible.
   */
  function initAnnouncementRotator() {
    var bars = document.querySelectorAll('.AnnouncementBar[data-announcement-autoplay="true"]');

    for (var i = 0; i < bars.length; i++) {
      (function (bar) {
        var messages = bar.querySelectorAll('.AnnouncementBar__Message');
        if (messages.length < 2) {
          return;
        }

        var speed = (parseFloat(bar.getAttribute('data-announcement-speed')) || 4) * 1000;
        var current = 0;

        setInterval(function () {
          messages[current].classList.remove('is-active');
          current = (current + 1) % messages.length;
          messages[current].classList.add('is-active');
        }, speed);
      })(bars[i]);
    }
  }

  /**
   * Footer columns -> accordions on mobile (<= 1007px). Runs only when the
   * footer has .Footer--collapsibleMobile. Desktop is untouched (CSS keeps
   * columns open and hides the toggles).
   */
  function initFooterAccordion() {
    var footer = document.querySelector('.Footer--collapsibleMobile');
    if (!footer) {
      return;
    }

    var mq = window.matchMedia('(max-width: 1007px)');
    var toggles = footer.querySelectorAll('[data-footer-toggle]');

    function toggle() {
      if (!mq.matches) {
        return;
      }
      var block = this.parentNode;
      var isOpen = block.classList.toggle('is-open');
      this.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('role', 'button');
      toggles[i].setAttribute('tabindex', '0');
      toggles[i].setAttribute('aria-expanded', 'false');
      toggles[i].addEventListener('click', toggle);
      toggles[i].addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.click();
        }
      });
    }
  }

  /**
   * PLP product-card image swipe (mobile/touch only). Updates the active dot
   * as the shopper swipes. The scroll listener only fires for the track being
   * swiped, so this stays cheap even with many cards on a collection page.
   */
  function initProductCardSwipe() {
    var swipes = document.querySelectorAll('.ProductItem__Swipe');

    for (var i = 0; i < swipes.length; i++) {
      (function (swipe) {
        var track = swipe.querySelector('.ProductItem__SwipeTrack');
        var dots = swipe.querySelectorAll('.ProductItem__SwipeDot');
        if (!track || dots.length < 2) {
          return;
        }

        var ticking = false;
        track.addEventListener('scroll', function () {
          if (ticking) {
            return;
          }
          ticking = true;
          window.requestAnimationFrame(function () {
            var index = Math.round(track.scrollLeft / track.clientWidth);
            if (index < 0) { index = 0; }
            if (index > dots.length - 1) { index = dots.length - 1; }
            for (var d = 0; d < dots.length; d++) {
              if (d === index) {
                dots[d].classList.add('is-active');
              } else {
                dots[d].classList.remove('is-active');
              }
            }
            ticking = false;
          });
        }, { passive: true });
      })(swipes[i]);
    }
  }

  /**
   * PDP sticky add-to-cart bar. Shows only when the real Add-to-cart button is
   * scrolled out of view; proxies clicks to the real button; mirrors price and
   * label/state. Hidden while a drawer is open (CSS via .no-scroll).
   */
  function initStickyAtc() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) {
      return;
    }

    var realButton = document.querySelector('.ProductForm__AddToCart');
    if (!realButton) {
      return;
    }

    var form = document.querySelector('.ProductForm');
    var trigger = bar.querySelector('[data-sticky-atc-trigger]');
    var priceEl = bar.querySelector('[data-sticky-atc-price]');
    var labelEl = bar.querySelector('[data-sticky-atc-label]');

    function sync() {
      if (trigger) {
        trigger.disabled = realButton.hasAttribute('disabled');
      }
      var realLabel = realButton.querySelector('span');
      if (labelEl && realLabel) {
        labelEl.textContent = realLabel.textContent;
      }
      var mainPrice = document.querySelector('.ProductMeta__Price');
      if (priceEl && mainPrice) {
        priceEl.textContent = mainPrice.textContent;
      }
    }

    if (trigger) {
      trigger.addEventListener('click', function () {
        realButton.click();
      });
    }

    if (form) {
      form.addEventListener('variant:changed', sync);
      form.addEventListener('product:added', sync);
    }

    sync();

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        var show = !entries[0].isIntersecting;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.documentElement.classList.toggle('sticky-atc-visible', show);
      }, { threshold: 0 });
      observer.observe(realButton);
    }
  }

  /**
   * Estimated delivery date (PDP). Reads today's date IN IST (Asia/Kolkata)
   * from the visitor's clock, adds the delivery window, and displays the result
   * — so the date is correct for every visitor regardless of their device
   * timezone, and fresh (not cached like a Liquid value). Fully wrapped in
   * try/catch: on any unsupported environment the line simply stays hidden.
   * Renders nothing if the element is absent (feature toggled off).
   */
  function initDeliveryEta() {
    var el = document.querySelector('[data-delivery-eta]');
    if (!el || !window.Intl || !window.Intl.DateTimeFormat) {
      return;
    }

    try {
      var textEl = el.querySelector('.DeliveryEta__Text') || el;
      var min = parseInt(el.getAttribute('data-min'), 10);
      var max = parseInt(el.getAttribute('data-max'), 10);
      if (isNaN(min)) { min = 7; }
      if (isNaN(max) || max < min) { max = min; }
      var skipSunday = el.getAttribute('data-skip-sunday') === 'true';
      var label = el.getAttribute('data-label') || 'Estimated delivery';

      // Today's calendar date IN IST, derived from the visitor's current instant.
      var todayParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Kolkata', year: 'numeric', month: 'numeric', day: 'numeric'
      }).formatToParts(new Date());
      var p = {};
      for (var i = 0; i < todayParts.length; i++) {
        if (todayParts[i].type !== 'literal') {
          p[todayParts[i].type] = parseInt(todayParts[i].value, 10);
        }
      }

      function addDays(days) {
        // Treat the IST date as plain UTC midnight, add days in UTC, and format
        // in UTC too — so no timezone can shift the displayed calendar date.
        var d = new Date(Date.UTC(p.year, p.month - 1, p.day));
        d.setUTCDate(d.getUTCDate() + days);
        if (skipSunday && d.getUTCDay() === 0) {
          d.setUTCDate(d.getUTCDate() + 1);
        }
        return d;
      }

      var fmt = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short'
      });
      var dateStr = (min === max)
        ? fmt.format(addDays(min))
        : fmt.format(addDays(min)) + ' – ' + fmt.format(addDays(max));

      textEl.textContent = label + ': ';
      var strong = document.createElement('strong');
      strong.textContent = dateStr;
      textEl.appendChild(strong);

      el.removeAttribute('hidden');
    } catch (e) {
      /* Unsupported environment — leave the line hidden, surface no error. */
    }
  }

  /**
   * Bucks currency switcher - hide inside the cart drawer.
   *
   * The Bucks app embed re-injects its switcher each time the drawer
   * re-renders (open, quantity change, item removal) without clearing the
   * previous one, so instances accumulate - three were showing at once.
   * The bug is in the app's own script, so this suppresses the widget in
   * the drawer rather than fixing the cause.
   *
   * Done as a stylesheet rule rather than by walking the DOM: the cascade
   * already applies to nodes inserted later, which is exactly the problem
   * here, so no MutationObserver is needed.
   *
   * Injected from here rather than added to theme.scss.liquid (244KB and
   * Shopify-compiled, where a syntax error would break site-wide styling)
   * or a new stylesheet (an extra render-blocking request). custom.js is
   * already loaded, and the drawer starts closed, so the deferred timing
   * causes no flash.
   *
   * Emitted as one rule per selector, not a comma-joined list: a single
   * unparseable selector would otherwise drop the whole rule.
   *
   * Every selector is scoped to #sidebar-cart. That scope is what keeps
   * prices and theme markup elsewhere from ever being matched.
   *
   * If the flags persist, Bucks has renamed its wrapper - add the new
   * class to SELECTORS below.
   */
  function initHideBucksInCart() {
    var SELECTORS = [
      '[class*="bucks" i]',
      '[id*="bucks" i]',
      '[class*="currency-switcher" i]',
      '[id*="currency-switcher" i]',
      '[class*="currency-converter" i]',
      '[id*="currency-converter" i]',
      '[class*="currency-selector" i]',
      '[id*="currency-selector" i]'
    ];

    var css = SELECTORS.map(function (selector) {
      return '#sidebar-cart ' + selector + '{display:none !important;}';
    }).join('');

    var style = document.createElement('style');
    style.setAttribute('data-chakkra', 'hide-bucks-in-cart');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function init() {
    initAnnouncementRotator();
    initFooterAccordion();
    initProductCardSwipe();
    initStickyAtc();
    initDeliveryEta();
    initHideBucksInCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
